import React, { useRef } from 'react';
import { ImageBlock } from '../../../core/ast/document.types';
import { storeAsset } from '../../../core/persistence/assetRepository';
import { Upload, ImageIcon, Scan } from 'lucide-react';

interface ImageBlockEditorProps {
  block: ImageBlock;
  onUpdate: (updates: Partial<ImageBlock['data']>) => void;
  onTriggerOCR?: (imagePreviewData: string) => void;
}

export const ImageBlockEditor: React.FC<ImageBlockEditorProps> = ({
  block,
  onUpdate,
  onTriggerOCR,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { previewDataUrl, caption, widthPercent = 80, alignment = 'center' } = block.data;

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const asset = await storeAsset(file, file.name);
      onUpdate({
        assetId: asset.id,
        previewDataUrl: asset.dataUrl,
        altText: file.name,
      });
    } catch (err) {
      console.error('Failed to store image asset:', err);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-3)',
        padding: 'var(--space-3)',
        backgroundColor: 'var(--bg-surface-subtle)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border-subtle)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
        <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
          <ImageIcon size={14} color="var(--primary-600)" />
          <span>Diagram & Image Block</span>
        </span>
        <input
          type="file"
          ref={fileInputRef}
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          style={{ display: 'none' }}
          onChange={handleFileUpload}
        />
        <div style={{ display: 'flex', gap: 4 }}>
          {previewDataUrl && onTriggerOCR && (
            <button
              type="button"
              className="btn btn-secondary btn-sm text-xs"
              onClick={() => onTriggerOCR(previewDataUrl)}
              title="Run Smart OCR Recognition on this image"
            >
              <Scan size={12} /> Scan with OCR
            </button>
          )}
          <button
            type="button"
            className="btn btn-secondary btn-sm text-xs"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload size={12} /> Upload Image
          </button>
        </div>
      </div>

      {previewDataUrl ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <img
            src={previewDataUrl}
            alt="Preview"
            style={{
              maxHeight: '140px',
              maxWidth: '100%',
              objectFit: 'contain',
              borderRadius: 'var(--radius-xs)',
              border: '1px solid var(--border-default)',
            }}
          />
        </div>
      ) : (
        <div
          onClick={() => fileInputRef.current?.click()}
          style={{
            padding: 'var(--space-4)',
            border: '1px dashed var(--border-strong)',
            borderRadius: 'var(--radius-sm)',
            textAlign: 'center',
            cursor: 'pointer',
            color: 'var(--text-muted)',
            fontSize: 'var(--font-size-xs)',
          }}
        >
          Click to choose image or experimental waveform chart
        </div>
      )}

      {/* Image Sizing and Caption */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 'var(--space-2)' }}>
        <div className="form-group">
          <label className="form-label" style={{ fontSize: '11px' }}>Figure Caption</label>
          <input
            type="text"
            className="form-input text-xs"
            value={caption || ''}
            onChange={(e) => onUpdate({ caption: e.target.value })}
            placeholder="e.g. Figure 1: Diode Circuit Diagram"
          />
        </div>
        <div className="form-group">
          <label className="form-label" style={{ fontSize: '11px' }}>Alignment</label>
          <select
            className="form-select text-xs"
            value={alignment}
            onChange={(e) => onUpdate({ alignment: e.target.value as any })}
          >
            <option value="center">Center</option>
            <option value="left">Left</option>
            <option value="right">Right</option>
          </select>
        </div>
        <div className="form-group">
          <label className="form-label" style={{ fontSize: '11px' }}>Width ({widthPercent}%)</label>
          <input
            type="range"
            min={20}
            max={100}
            step={5}
            value={widthPercent}
            onChange={(e) => onUpdate({ widthPercent: Number(e.target.value) })}
          />
        </div>
      </div>
    </div>
  );
};
